import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, CalendarClock, Shield, DollarSign, AlertTriangle,
  Layers, HardHat, Wrench, Scale, FileText, Loader2, Timer, Clock,
  CheckCircle2, Activity, ArrowUpRight, ArrowDownRight, Minus, GitBranch,
  Mail, Gavel, Truck, Sparkles, RefreshCw, Bot, ChevronDown, Database,
  ExternalLink
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LineChart, Line, Cell, PieChart, Pie,
  Legend, ReferenceLine
} from 'recharts';
import api from '@/lib/api';
import { useProject } from '@/contexts/ProjectContext';
import ModulesSnapshot from '@/components/dashboard/ModulesSnapshot';
import ManpowerSCurve from '@/components/dashboard/ManpowerSCurve';
import { ModuleHealthHeatmap, CrossModuleRiskRadar, CostVsScheduleMatrix } from '@/components/dashboard/DashboardWidgets';
import { PersonalizableSlot, WidgetSettingsGear, useWidgetOrder } from '@/components/dashboard/WidgetPersonalization';
import ScheduleSCurveChart from '@/components/charts/ScheduleSCurveChart';
import ProjectMilestonesPanel from '@/components/project/ProjectMilestonesPanel';
import ContractorPerformancePanel from '@/components/project/ContractorPerformancePanel';
import { EngineeringAnalytics, ProcurementAnalytics, ConstructionAnalytics } from '@/components/project/ModuleAnalyticsPanels';
import { usePanelTheme } from '@/components/project/panelTheme';
import ProjectKpiBuilder from '@/components/project/ProjectKpiBuilder';
import EquipmentAnalytics from '@/components/project/EquipmentAnalytics';
import ModuleDrilldownDrawer from '@/components/project/ModuleDrilldownDrawer';

const formatINR = (v) => {
  if (!v && v !== 0) return '-';
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (a >= 10000000) return `${sign}${(a / 10000000).toFixed(2)} Cr`;
  if (a >= 100000) return `${sign}${(a / 100000).toFixed(2)} L`;
  if (a >= 1000) return `${sign}${(a / 1000).toFixed(1)}K`;
  return `${sign}${a.toLocaleString('en-IN')}`;
};

const formatINRFull = (v) => {
  if (!v && v !== 0) return '-';
  return `₹${formatINR(v)}`;
};

const KPITrend = ({ value, threshold = 1.0, inverse = false }) => {
  const isGood = inverse ? value < threshold : value >= threshold;
  if (Math.abs(value - threshold) < 0.01) return <Minus className="w-3 h-3 text-slate-400" />;
  return isGood
    ? <ArrowUpRight className="w-3 h-3 text-emerald-400" />
    : <ArrowDownRight className="w-3 h-3 text-red-400" />;
};

const kpiColor = (v, t = 1.0, inv = false) => {
  if (v === null || v === undefined) return '#64748b';
  const good = inv ? v < t : v >= t;
  return good ? '#10b981' : v === t ? '#f59e0b' : '#ef4444';
};

const hiScoreColor = (s) => {
  if (s >= 80) return '#10b981';
  if (s >= 60) return '#f59e0b';
  return '#ef4444';
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0a0f1e] border border-[#3A3F4A] rounded-lg px-3 py-2 shadow-xl">
      <p className="text-[10px] text-slate-500 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>{p.name}: {typeof p.value === 'number' ? p.value.toFixed(2) : p.value}</p>
      ))}
    </div>
  );
};

const MiniCard = ({ title, icon: Icon, iconColor, children, className = '' }) => (
  <Card className={`bg-[#1F2937] border-[#3A3F4A] ${className}`}>
    <CardHeader className="pb-2 pt-3 px-4">
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded" style={{ backgroundColor: `${iconColor}15` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
        </div>
        <CardTitle className="text-xs text-slate-400 uppercase tracking-wider font-medium">{title}</CardTitle>
      </div>
    </CardHeader>
    <CardContent className="px-4 pb-3">{children}</CardContent>
  </Card>
);

const getRelativeTime = (dateStr) => {
  if (!dateStr) return { text: 'No data', stale: 2 };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { text: dateStr.slice(0, 10), stale: 0 };
  const now = new Date();
  const diffMs = now - d;
  const diffH = diffMs / 3600000;
  const diffD = diffH / 24;
  let text;
  if (diffH < 1) text = 'Just now';
  else if (diffH < 24) text = `${Math.floor(diffH)}h ago`;
  else if (diffD < 7) text = `${Math.floor(diffD)}d ago`;
  else if (diffD < 30) text = `${Math.floor(diffD / 7)}w ago`;
  else text = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  const stale = diffD > 30 ? 2 : diffD > 7 ? 1 : 0;
  return { text, stale };
};

const FreshnessChip = ({ module: m }) => {
  const { text, stale } = getRelativeTime(m.last_updated);
  const dotColor = stale === 2 ? '#ef4444' : stale === 1 ? '#f59e0b' : '#10b981';
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0A0E1A] border border-[#3A3F4A]">
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dotColor }} />
      <span className="text-[9px] text-slate-500">{m.label}</span>
      <span className="text-[9px] text-slate-400">{text}</span>
    </span>
  );
};

const FreshnessCard = ({ module: m }) => {
  const { text, stale } = getRelativeTime(m.last_updated);
  const borderColor = stale === 2 ? '#ef4444' : stale === 1 ? '#f59e0b' : '#10b981';
  return (
    <div className="p-2.5 rounded-lg bg-[#0A0E1A] border-l-2" style={{ borderColor }} data-testid={`freshness-${m.label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-slate-400 font-medium">{m.label}</span>
        <Database className="w-3 h-3 text-slate-600" />
      </div>
      <p className="text-xs text-white font-semibold">{text}</p>
      <p className="text-[9px] text-slate-500 mt-0.5">{m.record_count} records</p>
    </div>
  );
};

export default function ProjectDashboard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { projectId: urlProjectId } = useParams();
  const { selectedProject } = useProject();
  const projectId = urlProjectId || searchParams.get('projectId') || selectedProject?.project_id;
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [crossModuleRisks, setCrossModuleRisks] = useState(null);
  const [data, setData] = useState({
    project: null, schedule: null, costEvm: null, engineering: null,
    procurement: null, construction: null, resources: null, contracts: null,
    hse: null, raid: null, clm: null, swm: null, pwm: null
  });
  const [freshness, setFreshness] = useState(null);
  const [showFreshness, setShowFreshness] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setLastRefreshed(new Date());
    setRefreshing(false);
  };

  useEffect(() => {
    if (!projectId) return;
    fetchAllData();
  }, [projectId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get(`/projects/${projectId}/schedule`),
        api.get(`/projects/${projectId}/cost-evm`),
        api.get(`/projects/${projectId}/engineering`),
        api.get(`/projects/${projectId}/procurement`),
        api.get(`/projects/${projectId}/construction`),
        api.get(`/projects/${projectId}/resources`),
        api.get(`/projects/${projectId}/contracts`),
        api.get(`/projects/${projectId}/hse`),
        api.get(`/projects/${projectId}/raid`),
        api.get(`/clm/projects/${projectId}/clm-dashboard`),
        api.get(`/projects/${projectId}/dashboard`),
        api.get(`/v1/projects/${projectId}/swm/summary`).catch(() => null),
        api.get(`/v1/projects/${projectId}/pwm/summary`).catch(() => null),
      ]);

      const get = (i) => results[i]?.status === 'fulfilled' ? results[i].value?.data : null;

      setData({
        schedule: get(0), costEvm: get(1), engineering: get(2),
        procurement: get(3), construction: get(4), resources: get(5),
        contracts: get(6), hse: get(7), raid: get(8), clm: get(9),
        project: get(10)?.project || null, swm: get(11), pwm: get(12)
      });
      // Fetch data freshness in background
      api.get(`/projects/${projectId}/data-freshness`).then(r => setFreshness(r.data?.freshness)).catch(() => {});
      // Fetch cross-module risks in background
      api.get(`/v1/projects/${projectId}/raid/cross-module-risks?limit=20`).then(r => setCrossModuleRisks(r.data)).catch(() => {});
      setLastRefreshed(new Date());
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchForecast = async () => {
    setForecastLoading(true);
    try {
      const res = await api.get(`/clm/projects/${projectId}/ai-forecast`);
      setForecast(res.data);
    } catch (e) {
      console.error('Forecast error:', e);
    } finally {
      setForecastLoading(false);
    }
  };

  // Widget order hooks — must be at top level (not inside a conditional / early return)
  const overviewWidgetOrder = useWidgetOrder(projectId, 'overview', ['cost-schedule-matrix', 'risk-radar', 'health-heatmap']);
  const modulesWidgetOrder  = useWidgetOrder(projectId, 'modules',  ['modules-snapshot', 'manpower-s-curve', 'modules-heatmap', 'modules-radar']);


  const kpiSnap = useMemo(() => {
    const s = data.schedule?.kpis || {};
    const c = data.costEvm?.kpis || {};
    const p = data.project || {};
    // Canonical priority: live cost-EVM (from /cc/evm/current) > schedule KPIs > project static seed.
    // This keeps Overview aligned with the Earned Value Management page and Reports.
    const pick = (a, b, c2, d) => (a !== undefined && a !== null ? a : (b !== undefined && b !== null ? b : (c2 !== undefined && c2 !== null ? c2 : d)));
    return {
      hiScore: p.hi_score ?? Math.round(((s.spi || 1) * 50 + (c.cpi || 1) * 50)),
      spi: pick(c.spi, s.spi, p.spi, 1.0),
      cpi: pick(c.cpi, p.cpi, s.cpi, 1.0),
      bac: pick(c.bac, p.bac, undefined, 0),
      eac: pick(c.eac, p.eac, undefined, 0),
      planned: pick(c.plannedProgressPct, p.planned_pct, s.planned_pct, 0),
      actual: pick(c.physicalProgressPct, p.actual_pct, s.actual_pct, 0),
      earned: s.earned_pct ?? p.actual_pct ?? 0,
      openRisks: data.raid?.kpis?.open_risks ?? 0,
      pv: c.bcws ?? c.pv ?? 0, ev: c.bcwp ?? c.ev ?? 0, ac: c.acwp ?? c.ac ?? 0,
      cv: c.cv ?? 0, sv: c.sv ?? 0, vac: c.vac ?? 0,
      tcpi: c.tcpi ?? 1.0, etc: c.etc ?? 0
    };
  }, [data]);

  const [forecastMethod, setForecastMethod] = useState('linear'); // 'linear' | 'sshape' | 'cpi'
  const sCurveData = useMemo(() => {
    const raw = data.schedule?.s_curve || data.project?.schedule || [];
    if (!raw.length) return { rows: [], dataDateMonth: null };
    const now = new Date();
    const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
    // Find actual@today so Forecast starts where Actual leaves off
    let actualAtToday = 0;
    const rows = raw.map((d, i) => {
      const rowDate = d.date ? new Date(d.date) : null;
      const isFuture = rowDate ? rowDate > now : false;
      const planned  = +(d.planned_pct ?? 0).toFixed(1);
      const actual   = +(d.actual_pct  ?? 0).toFixed(1);
      if (rowDate && rowDate <= now && actual > actualAtToday) actualAtToday = actual;
      return {
        month: rowDate ? rowDate.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }) : `M${i + 1}`,
        _date: d.date,
        Planned:  planned,
        Actual:   isFuture ? null : actual,
        Forecast: null, // populated in the pass below
      };
    });
    // ── Extrapolate Planned to 100% across the full project window ──
    // Prefer the freshest, most-forward-looking date to bound the X-axis:
    // forecast_finish → baseline_finish → scheduled_finish → end_date. If
    // none of those exist, keep marching monthly until Planned hits 100%
    // (hard-capped at 120 months so a bad `lastPlan=0` never runs away).
    const lastPlan = rows.length ? rows[rows.length - 1].Planned : 0;
    const projEnd = data.project?.forecast_finish
                 || data.project?.baseline_finish
                 || data.project?.scheduled_finish
                 || data.project?.end_date;
    const lastDate = rows.length ? new Date(rows[rows.length - 1]._date) : null;
    if (lastPlan < 100 && lastDate) {
      const end = projEnd ? new Date(projEnd) : null;
      let monthsRemaining = end
        ? Math.max(1, (end.getFullYear() - lastDate.getFullYear()) * 12 + (end.getMonth() - lastDate.getMonth()))
        : Math.max(1, Math.ceil((100 - lastPlan) / 3));  // fallback: assume ~3%/month
      monthsRemaining = Math.min(monthsRemaining, 120);
      const step = (100 - lastPlan) / monthsRemaining;
      let cursor = new Date(lastDate);
      for (let k = 1; k <= monthsRemaining; k++) {
        cursor = new Date(cursor); cursor.setMonth(cursor.getMonth() + 1);
        rows.push({
          month: cursor.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
          _date: cursor.toISOString(),
          Planned:  +Math.min(100, lastPlan + step * k).toFixed(1),
          Actual:   null,
          Forecast: null,
        });
      }
    }
    // ── Forecast: same linear-to-100% shape as Planned across the full
    // macro-schedule window, but the CURVE is only PLOTTED from today+1
    // onwards (rows before today are set to null so the series starts at
    // the Actual→Forecast handoff point).
    const projectEnd = rows.length ? new Date(rows[rows.length - 1]._date) : null;
    const projectStart = rows.length ? new Date(rows[0]._date) : null;
    if (projectEnd && projectStart) {
      const totalMs = projectEnd - projectStart;
      // CPI is read from live cost-EVM feed for the "CPI-adjusted" mode.
      const cpiLive = Math.max(0.4, Math.min(1.2,
        data?.costEvm?.kpis?.cpi ?? data?.schedule?.kpis?.cpi ?? 1
      ));
      rows.forEach((r) => {
        const rd = r._date ? new Date(r._date) : null;
        if (!rd) return;
        let f;
        if (forecastMethod === 'cpi') {
          // CPI-adjusted forecast = Planned S-curve shape × CPI factor.
          // This preserves the exact same S-shape as the Planned curve (using
          // the backend's work-content-weighted distribution) but scales every
          // y-value by the live CPI so the terminal value lands at 100 × CPI.
          // Result: an S-shape (not a straight line) that visually diverges
          // from Planned by the cost-performance ratio.
          f = Math.min(100, (r.Planned ?? 0) * cpiLive);
        } else {
          // "linear"/"sshape" mode mirrors the Planned S-curve shape 1:1
          // (backend distributes work by task-weight producing a natural S).
          f = r.Planned;
        }
        r.Forecast = rd >= tomorrow ? +Math.min(100, Math.max(0, f ?? 0)).toFixed(1) : null;
      });
    }
    const dataDateMonth = now.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
    return { rows, dataDateMonth };
  }, [data, forecastMethod]);
  const sCurveRows = sCurveData.rows || [];
  const sCurveDataDate = sCurveData.dataDateMonth;

  const costTrend = useMemo(() => {
    const raw = data.costEvm?.trend || [];
    if (!raw.length) return [];
    return raw.map((d, i) => ({
      month: d.timestamp ? new Date(d.timestamp).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }) : `M${i + 1}`,
      PV: +(d.pv ?? 0).toFixed(0),
      EV: +(d.ev ?? 0).toFixed(0),
      AC: +(d.ac ?? 0).toFixed(0)
    }));
  }, [data]);

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#1E4E8C] animate-spin" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="project-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{data.project?.name || 'Project Overview'}</h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{data.project?.sector} | {data.project?.geography} | Phase: {data.project?.phase}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="refresh-dashboard-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1F2937] border border-[#3A3F4A] hover:border-[#1E4E8C]/40 text-slate-400 hover:text-white transition-all text-xs font-medium disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            {lastRefreshed && (
              <span className="text-[10px] text-slate-500">
                Refreshed at {lastRefreshed.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-5" data-testid="project-overview">
            {/* KPI Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="kpi-strip">
              {[
                { label: 'HI Score', value: kpiSnap.hiScore, fmt: v => v, color: hiScoreColor(kpiSnap.hiScore), sub: 'Health Index' },
                { label: 'SPI', value: kpiSnap.spi, fmt: v => v.toFixed(2), color: kpiColor(kpiSnap.spi), sub: kpiSnap.spi >= 1 ? 'On track' : 'Behind' },
                { label: 'CPI', value: kpiSnap.cpi, fmt: v => v.toFixed(2), color: kpiColor(kpiSnap.cpi), sub: kpiSnap.cpi >= 1 ? 'Under budget' : 'Over budget' },
                { label: 'BAC', value: kpiSnap.bac, fmt: v => formatINRFull(v), color: '#3b82f6', sub: 'Budget at Completion' },
                { label: 'EAC', value: kpiSnap.eac, fmt: v => formatINRFull(v), color: kpiSnap.eac > kpiSnap.bac ? '#ef4444' : '#10b981', sub: 'Est. at Completion' },
                { label: 'Planned %', value: kpiSnap.planned, fmt: v => `${v.toFixed(1)}%`, color: '#3b82f6', sub: 'Schedule' },
                { label: 'Actual %', value: kpiSnap.actual, fmt: v => `${v.toFixed(1)}%`, color: kpiSnap.actual >= kpiSnap.planned ? '#10b981' : '#ef4444', sub: 'Progress' },
                { label: 'Open Risks', value: kpiSnap.openRisks, fmt: v => v, color: kpiSnap.openRisks > 5 ? '#ef4444' : '#f59e0b', sub: 'Active risks' },
              ].map((k, i) => (
                <motion.div key={k.label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Card className="bg-[#1F2937] border-[#3A3F4A] h-full">
                    <CardContent className="p-3">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{k.label}</p>
                      <p className="text-xl font-bold" style={{ color: k.color }}>{k.fmt(k.value)}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{k.sub}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>


            {/* S-Curve + Cost & EVM */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Schedule S-Curve — identical chart and computation as the
                  Schedule module, with a daily / weekly / monthly x-axis. */}
              <ScheduleSCurveChart
                url={projectId ? `/v1/projects/${projectId}/overview/s-curve` : null}
                title="Schedule S-Curve (Plan vs Actual vs Forecast)"
                height={260}
                defaultInterval="week"
                compact
                projectId={projectId}
                testIdPrefix="project-scurve"
              />

              {/* Cost & EVM Performance */}
              <EvmPerformanceCard kpi={kpiSnap} fmt={formatINRFull} projectId={projectId} />
            </div>

            {/* Milestones + Contractor performance — Overview tab (user request) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <ProjectMilestonesPanel projectId={projectId} />
              <ContractorPerformancePanel projectId={projectId} />
            </div>

            {/* Module analytics — Engineering, Procurement, Fab & Construction */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="module-analytics-row">
              <EngineeringAnalytics data={data.engineering} projectId={projectId} />
              <ProcurementAnalytics data={data.procurement} projectId={projectId} />
              <ConstructionAnalytics data={data.construction} projectId={projectId} />
            </div>

            {/* Manpower S-Curve — from the F&C schedule, with contractor/trade filters */}
            <ManpowerSCurve projectId={projectId} />

            {/* Equipment analytics — register utilisation + F&C-derived demand */}
            <EquipmentAnalytics projectId={projectId} />

            {/* Build a KPI with Cognara — collapsible right-hand panel */}
            <ProjectKpiBuilder projectId={projectId} asPanel />


            {/* Deeper dashboard widgets */}
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Executive insight widgets</p>
              <WidgetSettingsGear projectId={projectId} scope="overview" widgets={[
                { key: 'cost-schedule-matrix', label: 'Cost × Schedule Matrix' },
                { key: 'risk-radar',           label: 'Cross-Module Risk Radar' },
                { key: 'health-heatmap',       label: 'Module Health Heatmap' },
              ]} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {overviewWidgetOrder.map(key => {
                if (key === 'cost-schedule-matrix') return (
                  <PersonalizableSlot key={key} projectId={projectId} scope="overview" widgetKey="cost-schedule-matrix">
                    <CostVsScheduleMatrix schedule={{ kpis: { spi: kpiSnap.spi } }} costEvm={{ kpis: { cpi: kpiSnap.cpi, bac: kpiSnap.bac } }} />
                  </PersonalizableSlot>
                );
                if (key === 'risk-radar') return (
                  <PersonalizableSlot key={key} projectId={projectId} scope="overview" widgetKey="risk-radar">
                    <CrossModuleRiskRadar raidData={data.raid} crossModuleRisks={crossModuleRisks} />
                  </PersonalizableSlot>
                );
                if (key === 'health-heatmap') return (
                  <PersonalizableSlot key={key} projectId={projectId} scope="overview" widgetKey="health-heatmap">
                    <ModuleHealthHeatmap data={data} />
                  </PersonalizableSlot>
                );
                return null;
              })}
            </div>
        </div>
      </div>
    </DashboardLayout>
  );
}


/* ── Cost & EVM Performance — theme-aware, animated PV/EV/AC bars ───────── */
function EvmPerformanceCard({ kpi, fmt, projectId }) {
  const T = usePanelTheme();
  const [drill, setDrill] = useState(false);
  const bars = [
    { label: 'PV', hint: 'Planned Value', value: kpi.pv, color: T.accent },
    { label: 'EV', hint: 'Earned Value',  value: kpi.ev, color: T.good },
    { label: 'AC', hint: 'Actual Cost',   value: kpi.ac, color: T.warn },
  ];
  const maxVal = Math.max(kpi.pv, kpi.ev, kpi.ac, 1);
  const metrics = [
    { label: 'CV',   value: fmt(kpi.cv),  good: kpi.cv >= 0 },
    { label: 'SV',   value: fmt(kpi.sv),  good: kpi.sv >= 0 },
    { label: 'VAC',  value: fmt(kpi.vac), good: kpi.vac >= 0 },
    { label: 'TCPI', value: (kpi.tcpi ?? 0).toFixed(2), good: (kpi.tcpi ?? 0) <= 1.1 },
    { label: 'ETC',  value: fmt(kpi.etc), good: true },
  ];
  return (
    <div className="rounded-2xl overflow-hidden" data-testid="evm-performance-card"
         style={{ background: T.card, border: `1px solid ${T.border}`,
                  boxShadow: T.isDark ? 'none' : '0 1px 2px rgba(15,23,42,0.04)' }}>
      <div className="px-5 pt-4 pb-3" style={{ background: T.sheen, borderBottom: `1px solid ${T.border}` }}>
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: `${T.good}1F` }}>
            <DollarSign className="w-3.5 h-3.5" style={{ color: T.good }} />
          </span>
          <div>
            <h3 className="text-sm font-semibold leading-tight" style={{ color: T.text }}>Cost &amp; EVM Performance</h3>
            <p className="text-[10px]" style={{ color: T.muted }}>
              CPI {(kpi.cpi ?? 1).toFixed(2)} · SPI {(kpi.spi ?? 1).toFixed(2)} · BAC {fmt(kpi.bac)}
            </p>
          </div>
          <button type="button" onClick={() => setDrill(true)}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold"
            style={{ background: `${T.good}18`, color: T.good, border: `1px solid ${T.good}44` }}
            data-testid="evm-drilldown-btn">
            <Layers className="w-3 h-3" /> L2 · L3
          </button>
          <div className="text-right">
            <div className="text-lg font-bold font-mono leading-none"
                 style={{ color: (kpi.vac ?? 0) >= 0 ? T.good : T.bad }}>{fmt(kpi.eac)}</div>
            <div className="text-[9px] uppercase tracking-wider" style={{ color: T.muted }}>forecast at completion</div>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {bars.map((b, i) => (
          <div key={b.label}>
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span style={{ color: T.text2 }}><b style={{ color: T.text }}>{b.label}</b> · {b.hint}</span>
              <span className="font-mono" style={{ color: T.text }}>{fmt(b.value)}</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ background: T.inner }}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${(b.value / maxVal) * 100}%` }}
                transition={{ duration: 0.9, ease: 'easeOut', delay: i * 0.08 }}
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${b.color}, ${b.color}AA)` }} />
            </div>
          </div>
        ))}
        <div className="grid grid-cols-5 gap-2 pt-2" style={{ borderTop: `1px solid ${T.border}` }}>
          {metrics.map((m, i) => (
            <motion.div key={m.label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.04 }}
              className="text-center rounded-xl py-2 transition-transform duration-200 hover:-translate-y-0.5"
              style={{ background: T.inner, border: `1px solid ${T.border}` }}>
              <p className="text-[9px] uppercase tracking-wider" style={{ color: T.muted }}>{m.label}</p>
              <p className="text-xs font-mono font-semibold" style={{ color: m.good ? T.good : T.bad }}>{m.value}</p>
            </motion.div>
          ))}
        </div>
      </div>
      {drill && (
        <ModuleDrilldownDrawer projectId={projectId} module="cost" onClose={() => setDrill(false)} />
      )}
    </div>
  );
}
